import { store } from 'redux/store';
import i18n from 'i18n/config';
import { getHoldingClassById } from 'modules/partners/api/getHoldingClassById';
import { getHoldingClasses } from 'modules/partners/api/getHoldingClasses';
import { useCallback, useEffect, useState } from 'react';
import { MultiValue, SingleValue } from 'react-select';

import { HoldingClass, SelectorOption } from 'types';
import { ownNumerationOption } from '../constants/ownNumerationOption';

type UseGetNumerationClassOptionsProps = {
  societyId: string;
  numerationClassId: string | null;
};

const FIELDS = 'name';

const mapHoldingClassesToOptions = (
  holdingClasses: HoldingClass[]
): SelectorOption[] => {
  const options = holdingClasses.map((holdingClass) => ({
    value: holdingClass._id,
    label: `${holdingClass.name} (${i18n.t('NumberFromTheLastClassPar')} "${
      holdingClass.name
    }")`,
  }));

  options.push(ownNumerationOption);

  return options;
};

export const useGetNumerationOptions = ({
  societyId,
  numerationClassId,
}: UseGetNumerationClassOptionsProps) => {
  const [selectedNumerationClass, setSelectedNumerationClass] =
    useState<string>('');
  const [selectedNumerationClassOption, setSelectedNumerationClassOption] =
    useState<SelectorOption | null>(null);
  const [numerationOptions, setNumerationOptions] = useState<SelectorOption[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const handleChangeNumerationClassOption = (
    selectedOption: MultiValue<SelectorOption> | SingleValue<SelectorOption>
  ) => {
    if (!selectedOption) return;
    if (selectedOption instanceof Array) return;

    setSelectedNumerationClassOption(selectedOption);
    setSelectedNumerationClass(selectedOption.value);
  };

  const handleFetchHoldingClassByTerm = async (searchTerm: string) => {
    if (!searchTerm || !societyId) return;

    try {
      const holdingClasses = await store.dispatch(
        getHoldingClasses({
          societyId,
          params: { fields: FIELDS, searchTerm },
        })
      );

      return mapHoldingClassesToOptions(holdingClasses);
    } catch (error) {
      setIsError(true);
    }
  };

  const fetchHoldingClasses = useCallback(async () => {
    if (!societyId) return;

    setIsLoading(true);
    setIsError(false);

    try {
      if (numerationClassId) {
        const holdingClass = await store.dispatch(
          getHoldingClassById({
            holdingClassId: numerationClassId,
          })
        );

        const selectedOption: SelectorOption = {
          value: holdingClass._id,
          label: `${holdingClass.name} (${i18n.t(
            'NumberFromTheLastClassPar'
          )} "${holdingClass.name}")`,
        };

        setSelectedNumerationClassOption(selectedOption);
        setSelectedNumerationClass(selectedOption?.value || '');
        setIsLoading(false);
        return;
      }

      const holdingClasses = await store.dispatch(
        getHoldingClasses({
          societyId,
          params: { fields: FIELDS },
        })
      );

      const mappedOptions = mapHoldingClassesToOptions(holdingClasses);
      setNumerationOptions(mappedOptions);

      const firstOption = mappedOptions[0] || null;
      setSelectedNumerationClassOption(firstOption);
      setSelectedNumerationClass(firstOption?.value || '');

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setIsError(true);
    }
  }, [numerationClassId, societyId]);

  useEffect(() => {
    fetchHoldingClasses();
  }, [fetchHoldingClasses]);

  return {
    data: {
      selectedNumerationClass,
      selectedNumerationClassOption,
      numerationOptions,
    },
    actions: {
      handleChangeNumerationClassOption,
      handleFetchHoldingClassByTerm,
    },
    isLoading,
    isError,
  };
};
